/** `jumbo.com` from ` https://www.Jumbo.com/nl/winkels?x=1 `; null when it is not a hostname. */
export const normaliseDomain = (input: string): string | null => {
  const trimmed = input.trim().toLowerCase();

  if (!trimmed) return null;

  let host = trimmed.replace(/^[a-z]+:\/\//, '');

  [host] = host.split(/[/?#]/);
  host = host.replace(/^www\./, '').replace(/\.$/, '');

  const isHostname = /^(?!-)(?:[a-z0-9-]{1,63}\.)+[a-z]{2,}$/.test(host) && host.length <= 253;

  return isHostname ? host : null;
};

/**
 * Complete mailbox domains, not provider labels. `me` would also reject
 * `inclusion.me.uk`, and a short suffix list would accept `outlook.com.ar`.
 * Subdomains of these hosts count too.
 */
const FREE_MAIL_DOMAINS = [
  'me.com',
  'mac.com',
  'icloud.com',
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'hotmail.de',
  'hotmail.nl',
  'hotmail.be',
  'hotmail.es',
  'hotmail.it',
  'live.com',
  'live.nl',
  'live.be',
  'live.fr',
  'live.co.uk',
  'live.com.au',
  'msn.com',
  'outlook.com',
  'outlook.de',
  'outlook.fr',
  'outlook.es',
  'outlook.it',
  'outlook.nl',
  'outlook.be',
  'outlook.co.uk',
  'outlook.com.au',
  'outlook.com.ar',
  'outlook.com.br',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'yahoo.es',
  'yahoo.it',
  'yahoo.co.jp',
  'yahoo.com.au',
  'yahoo.com.br',
  'yahoo.com.ar',
  'ymail.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'gmx.com',
  'gmx.net',
  'gmx.de',
];

/** Temporary inboxes. Same well-known set the API refuses for a company website. */
const TEMP_MAIL_DOMAINS = [
  'mailinator.com',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'guerrillamail.com',
  'sharklasers.com',
  '10minutemail.com',
  '10minutemail.net',
  'temp-mail.org',
  'tempmail.com',
  'tempmail.dev',
  'tempr.email',
  'throwawaymail.com',
  'trashmail.com',
  'getnada.com',
  'dispostable.com',
  'maildrop.cc',
  'mohmal.com',
  'fakeinbox.com',
  'burnermail.io',
  'emailondeck.com',
  'mailnesia.com',
  'mytemp.email',
  'minutemail.com',
  '33mail.com',
  'mailsac.com',
  'inboxkitten.com',
  'moakt.com',
  'tempail.com',
  'crazymailing.com',
  'spamgourmet.com',
  'anonbox.net',
];

/** The existing free-trial rule: a work address, not a free or temporary mailbox. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function mailboxDomain(email: string) {
  return email.trim().toLowerCase().split('@').pop() ?? '';
}

function matchesDomain(domain: string, blocked: string) {
  return domain === blocked || domain.endsWith(`.${blocked}`);
}

function isFreeMailDomain(domain: string) {
  return FREE_MAIL_DOMAINS.some((blocked) => matchesDomain(domain, blocked));
}

function isTemporaryMailbox(domain: string) {
  return TEMP_MAIL_DOMAINS.some((blocked) => matchesDomain(domain, blocked));
}

export const isWorkEmail = (email: string) => {
  const value = email.trim().toLowerCase();
  const domain = mailboxDomain(value);

  return EMAIL_SHAPE.test(value) && !isFreeMailDomain(domain) && !isTemporaryMailbox(domain);
};
